// FILE: src/lib/supabase.js
// COMPLETE UPDATED VERSION - Enhanced with proper client/server separation

import { createClient } from "@supabase/supabase-js";

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

export const supabase = createClient(supabaseUrl, supabaseAnonKey);

// ===== USER MANAGEMENT =====

async function createUserProfile(clerkUser) {
  try {
    console.log('Creating/getting user profile for Clerk ID:', clerkUser.id);
    
    // Check if user already exists and return full profile
    const { data: existingUser, error: existingError } = await supabase
      .from('users')
      .select('*')
      .eq('clerk_user_id', clerkUser.id)
      .single()

    if (existingUser && !existingError) {
      console.log('Found existing user profile:', existingUser);
      return existingUser;
    }

    // Only create if user doesn't exist
    if (existingError && existingError.code !== 'PGRST116') {
      console.error('Error checking existing user:', existingError);
      return null;
    }

    console.log('Creating new user profile...');
    
    const { data, error } = await supabase
      .from('users')
      .insert([
        {
          clerk_user_id: clerkUser.id,
          username: clerkUser.firstName && clerkUser.lastName 
            ? `${clerkUser.firstName} ${clerkUser.lastName}` 
            : clerkUser.username || 'Anonymous Player',
          email: clerkUser.emailAddresses?.[0]?.emailAddress,
          total_games: 0,
          games_won: 0,
          games_lost: 0,
          total_score: 0,
          average_score: 0,
          highest_score: 0,
          rank_points: 1000
        }
      ])
      .select('*')
      .single()

    if (error) {
      console.error('Error creating user profile:', error)
      return null
    }

    console.log('Created new user profile:', data)
    return data
  } catch (err) {
    console.error('Error in createUserProfile:', err)
    return null
  }
}

async function getUserProfile(clerkUserId) {
  try {
    console.log('Getting user profile for Clerk ID:', clerkUserId);
    
    const { data, error } = await supabase
      .from('users')
      .select('*')
      .eq('clerk_user_id', clerkUserId)
      .single()

    if (error) {
      console.error('Error getting user profile:', error)
      return null
    }

    console.log('Retrieved user profile:', data);
    return data
  } catch (err) {
    console.error('Error in getUserProfile:', err)
    return null
  }
}

// ===== GAME MANAGEMENT =====

// Save single-player game results (client-side only)
async function saveSinglePlayerGameResult(gameData) {
  try {
    console.log('💾 Saving single-player game result:', gameData);

    // Validate that this is actually a single-player game
    if (gameData.gameMode === 'multiplayer') {
      console.warn('⚠️ Attempted to save multiplayer game on client-side. This should be handled by the server.');
      return false;
    }

    // Validate required data
    if (!gameData || !gameData.players || gameData.players.length === 0) {
      console.error('❌ Invalid game data: missing players');
      return false;
    }

    // Ensure all players have valid user IDs
    const validPlayers = [];
    for (const player of gameData.players) {
      const userId = player.userId || player.supabaseUserId;
      if (!userId || player.isAI) {
        console.log('ℹ️ Skipping AI or unauthenticated player:', player);
        continue;
      }
      
      // Verify user exists in database
      const { data: userExists } = await supabase
        .from('users')
        .select('id')
        .eq('id', userId)
        .single();
      
      if (!userExists) {
        console.warn('⚠️ User not found in database:', userId);
        continue;
      }
      
      validPlayers.push({
        ...player,
        userId: userId,
        supabaseUserId: userId
      });
    }

    if (validPlayers.length === 0) {
      console.log('ℹ️ No authenticated players - skipping single-player game save');
      return false;
    }

    console.log('✅ Valid players for save:', validPlayers.length);

    // Sort players by score to determine positions
    const sortedPlayers = validPlayers.sort((a, b) => {
      const scoreA = a.finalScore || a.score || 0;
      const scoreB = b.finalScore || b.score || 0;
      return scoreB - scoreA;
    });

    // Determine winner
    const winner = sortedPlayers[0];
    const winnerId = winner?.userId || null;

    // 1. Create the game record
    const gameRecord = {
      game_type: gameData.gameType || 'casual',
      player_count: validPlayers.length,
      pucks_per_turn: gameData.pucksPerTurn || 5,
      status: 'completed',
      winner_id: winnerId,
      total_rounds: gameData.totalRounds || gameData.rounds || 1,
      finished_at: new Date().toISOString(),
      game_data: {
        ...gameData,
        players: sortedPlayers,
        timestamp: new Date().toISOString(),
        version: '1.0'
      }
    };

    const { data: game, error: gameError } = await supabase
      .from('games')
      .insert([gameRecord])
      .select()
      .single();

    if (gameError) {
      console.error('❌ Error creating single-player game record:', gameError);
      return false;
    }

    console.log('✅ Single-player game record created with ID:', game.id);

    // 2. Save each player's results
    const playerResults = sortedPlayers.map((player, index) => {
      const finalScore = player.finalScore || player.score || 0;
      const pucksThrown = player.pucksThrown || gameData.pucksPerTurn || 5;
      const accuracy = player.accuracy || (pucksThrown > 0 ? Math.min(100, Math.max(0, (finalScore / pucksThrown) * 10)) : 0);
      
      // Calculate rank change for ranked games
      let rankChange = 0;
      if (gameData.gameType === 'ranked') {
        const position = index + 1;
        const totalPlayers = sortedPlayers.length;
        
        if (position === 1) {
          rankChange = Math.floor(50 / totalPlayers) + 10;
        } else if (position <= totalPlayers / 2) {
          rankChange = Math.floor(20 / totalPlayers);
        } else {
          rankChange = -Math.floor(30 / totalPlayers);
        }
      }

      return {
        game_id: game.id,
        user_id: player.userId,
        player_position: index + 1,
        final_score: finalScore,
        pucks_thrown: pucksThrown,
        accuracy_percentage: Math.round(accuracy),
        rank_change: rankChange
      };
    });

    const { error: playersError } = await supabase
      .from('game_players')
      .insert(playerResults);

    if (playersError) {
      console.error('❌ Error saving single-player results:', playersError);
      await supabase.from('games').delete().eq('id', game.id);
      return false;
    }

    console.log('✅ Single-player results saved for', playerResults.length, 'players');

    // 3. Update user statistics
    for (const result of playerResults) {
      const isWinner = result.player_position === 1;
      
      const { error: updateError } = await supabase
        .from('users')
        .update({
          total_games: supabase.raw('total_games + 1'),
          games_won: supabase.raw(`games_won + ${isWinner ? 1 : 0}`),
          games_lost: supabase.raw(`games_lost + ${isWinner ? 0 : 1}`),
          total_score: supabase.raw(`total_score + ${result.final_score}`),
          highest_score: supabase.raw(`GREATEST(highest_score, ${result.final_score})`),
          rank_points: supabase.raw(`GREATEST(0, rank_points + ${result.rank_change})`),
          updated_at: new Date().toISOString()
        })
        .eq('id', result.user_id);

      if (updateError) {
        console.warn('⚠️ Error updating user stats:', updateError);
      }
    }

    console.log('🎉 Single-player game result saved successfully!');
    return true;

  } catch (err) {
    console.error('❌ Critical error in saveSinglePlayerGameResult:', err);
    return false;
  }
}

// Legacy saveGameResult function - now routes to appropriate save method
async function saveGameResult(gameData) {
  try {
    console.log('🎯 Routing game save request:', gameData?.gameMode);

    // Route multiplayer games to server-side handling
    if (gameData?.gameMode === 'multiplayer') {
      console.warn('⚠️ Multiplayer games should be saved server-side. Client-side save ignored.');
      console.log('ℹ️ The server will handle this game save automatically when the game ends.');
      return false;
    }
    
    // Handle single-player games on client-side
    return await saveSinglePlayerGameResult(gameData);

  } catch (err) {
    console.error('❌ Error in saveGameResult routing:', err);
    return false;
  }
}

// ===== LEADERBOARD & STATISTICS =====

async function getLeaderboard(limit = 10) {
  try {
    const { data, error } = await supabase
      .from('users')
      .select(`
        username,
        total_games,
        games_won,
        games_lost,
        average_score,
        highest_score,
        rank_points
      `)
      .gt('total_games', 0)
      .order('rank_points', { ascending: false })
      .order('games_won', { ascending: false })
      .limit(limit)

    if (error) {
      console.error('Error getting leaderboard:', error)
      return []
    }

    return data.map(user => ({
      ...user,
      win_rate: user.total_games > 0 
        ? Math.round((user.games_won / user.total_games) * 100)
        : 0
    }))
  } catch (err) {
    console.error('Error in getLeaderboard:', err)
    return []
  }
}

async function getUserGameHistory(clerkUserId, limit = 20) {
  try {
    const { data: userData, error: userError } = await supabase
      .from('users')
      .select('id')
      .eq('clerk_user_id', clerkUserId)
      .single()

    if (userError || !userData) {
      console.error('Error getting user ID for game history:', userError)
      return []
    }

    const { data, error } = await supabase
      .from('game_players')
      .select(`
        game_id,
        final_score,
        player_position,
        pucks_thrown,
        accuracy_percentage,
        created_at,
        games (
          id,
          game_type,
          player_count,
          pucks_per_turn,
          total_rounds,
          finished_at
        )
      `)
      .eq('user_id', userData.id)
      .order('created_at', { ascending: false })
      .limit(limit)

    if (error) {
      console.error('Error getting user game history:', error)
      return []
    }

    return data.map(match => ({
      ...match,
      game_id: match.games?.id || match.game_id
    }))

  } catch (err) {
    console.error('Error in getUserGameHistory:', err)
    return []
  }
}

async function getDetailedMatchInfo(gameId) {
  try {
    console.log('Fetching detailed match info for game ID:', gameId);

    // Get the main game record
    const { data: gameData, error: gameError } = await supabase
      .from('games')
      .select(`
        id,
        game_type,
        player_count,
        pucks_per_turn,
        total_rounds,
        finished_at,
        created_at,
        game_data,
        winner_id
      `)
      .eq('id', gameId)
      .single();

    if (gameError) {
      console.error('Error fetching game data:', gameError);
      throw new Error('Failed to fetch game information');
    }

    // Get all players who participated
    const { data: playersData, error: playersError } = await supabase
      .from('game_players')
      .select(`
        user_id,
        player_position,
        final_score,
        pucks_thrown,
        accuracy_percentage,
        rank_change,
        users (
          username,
          clerk_user_id
        )
      `)
      .eq('game_id', gameId)
      .order('player_position', { ascending: true });

    if (playersError) {
      console.error('Error fetching players data:', playersError);
      throw new Error('Failed to fetch players information');
    }

    // Get winner information
    let winner = null;
    if (gameData.winner_id) {
      const { data: winnerData } = await supabase
        .from('users')
        .select('username, clerk_user_id')
        .eq('id', gameData.winner_id)
        .single();
      
      winner = winnerData;
    }

    // Extract player colors from game_data if available
    const playerColors = {};
    if (gameData.game_data && gameData.game_data.players) {
      gameData.game_data.players.forEach((player, index) => {
        if (player.color) {
          playerColors[player.userId || player.supabaseUserId] = player.color;
        }
      });
    }

    // Enhance players data
    const enhancedPlayers = playersData.map(player => ({
      ...player,
      username: player.users?.username || 'Unknown Player',
      clerk_user_id: player.users?.clerk_user_id,
      color: playerColors[player.user_id] || null,
      is_current_user: false
    }));

    const matchDetails = {
      game: gameData,
      players: enhancedPlayers,
      winner: winner,
      total_players: playersData.length
    };

    console.log('Match details loaded successfully:', matchDetails);
    return matchDetails;

  } catch (error) {
    console.error('Error in getDetailedMatchInfo:', error);
    throw error;
  }
}

// ===== TOURNAMENT MANAGEMENT =====

async function createTournament(tournamentData, creatorClerkId) {
  try {
    console.log('Creating tournament:', tournamentData);

    // Get creator's user ID
    const { data: creator } = await supabase
      .from('users')
      .select('id')
      .eq('clerk_user_id', creatorClerkId)
      .single();

    if (!creator) {
      throw new Error('Creator not found');
    }

    const { data: tournament, error } = await supabase
      .from('tournaments')
      .insert([{
        name: tournamentData.name,
        description: tournamentData.description,
        entry_fee: tournamentData.entry_fee || 0,
        prize_pool: tournamentData.prize_pool || 0,
        max_players: tournamentData.max_players || 8,
        status: 'open',
        bracket_data: {},
        created_by: creator.id,
        starts_at: tournamentData.starts_at,
        ends_at: tournamentData.ends_at
      }])
      .select()
      .single();

    if (error) {
      console.error('Error creating tournament:', error);
      return null;
    }

    console.log('Tournament created:', tournament);
    return tournament;

  } catch (err) {
    console.error('Error in createTournament:', err);
    return null;
  }
}

async function joinTournament(tournamentId, clerkUserId) {
  try {
    // Get user's Supabase ID
    const { data: user } = await supabase
      .from('users')
      .select('id')
      .eq('clerk_user_id', clerkUserId)
      .single();

    if (!user) {
      throw new Error('User not found');
    }

    // Check if already registered
    const { data: existing } = await supabase
      .from('tournament_participants')
      .select('id')
      .eq('tournament_id', tournamentId)
      .eq('user_id', user.id)
      .single();

    if (existing) {
      return { success: false, message: 'Already registered for this tournament' };
    }

    // Register for tournament
    const { error } = await supabase
      .from('tournament_participants')
      .insert([{
        tournament_id: tournamentId,
        user_id: user.id,
        registered_at: new Date().toISOString()
      }]);

    if (error) {
      console.error('Error joining tournament:', error);
      return { success: false, message: 'Failed to join tournament' };
    }

    return { success: true, message: 'Successfully joined tournament' };

  } catch (err) {
    console.error('Error in joinTournament:', err);
    return { success: false, message: 'Error joining tournament' };
  }
}

async function getTournaments(status = null) {
  try {
    let query = supabase
      .from('tournaments')
      .select(`
        *,
        tournament_participants (
          user_id,
          users (
            username
          )
        )
      `)
      .order('created_at', { ascending: false });

    if (status) {
      query = query.eq('status', status);
    }

    const { data, error } = await query;

    if (error) {
      console.error('Error getting tournaments:', error);
      return [];
    }

    return data.map(tournament => ({
      ...tournament,
      participant_count: tournament.tournament_participants?.length || 0,
      participants: tournament.tournament_participants?.map(p => p.users?.username) || []
    }));

  } catch (err) {
    console.error('Error in getTournaments:', err);
    return [];
  }
}

// ===== ENHANCED STATISTICS & UTILITIES =====

// Get user statistics with enhanced calculations
async function getUserStatistics(clerkUserId) {
  try {
    const userProfile = await getUserProfile(clerkUserId);
    if (!userProfile) {
      return null;
    }

    // Get detailed statistics
    const { data: gameStats, error } = await supabase
      .from('game_players')
      .select(`
        final_score,
        player_position,
        pucks_thrown,
        accuracy_percentage,
        games (
          game_type,
          finished_at
        )
      `)
      .eq('user_id', userProfile.id);

    if (error) {
      console.error('Error getting user statistics:', error);
      return userProfile;
    }

    // Calculate enhanced statistics
    const totalGames = gameStats.length;
    const wins = gameStats.filter(g => g.player_position === 1).length;
    const losses = totalGames - wins;
    const totalScore = gameStats.reduce((sum, g) => sum + g.final_score, 0);
    const averageScore = totalGames > 0 ? totalScore / totalGames : 0;
    const highestScore = totalGames > 0 ? Math.max(...gameStats.map(g => g.final_score)) : 0;
    const totalPucksThrown = gameStats.reduce((sum, g) => sum + g.pucks_thrown, 0);
    const averageAccuracy = totalGames > 0 
      ? gameStats.reduce((sum, g) => sum + g.accuracy_percentage, 0) / totalGames 
      : 0;

    // Recent performance (last 10 games)
    const recentGames = gameStats
      .sort((a, b) => new Date(b.games.finished_at) - new Date(a.games.finished_at))
      .slice(0, 10);
    const recentWins = recentGames.filter(g => g.player_position === 1).length;
    const recentWinRate = recentGames.length > 0 ? (recentWins / recentGames.length) * 100 : 0;

    return {
      ...userProfile,
      statistics: {
        totalGames,
        wins,
        losses,
        winRate: totalGames > 0 ? (wins / totalGames) * 100 : 0,
        totalScore,
        averageScore: Math.round(averageScore * 100) / 100,
        highestScore,
        totalPucksThrown,
        averageAccuracy: Math.round(averageAccuracy * 100) / 100,
        recentWinRate: Math.round(recentWinRate * 100) / 100,
        recentGames: recentGames.length
      }
    }
  } catch (err) {
    console.error('Error in getUserStatistics:', err);
    return null;
  }
}

// Get recent matches across all users (for dashboard)
async function getRecentMatches(limit = 10) {
  try {
    const { data, error } = await supabase
      .from('games')
      .select(`
        id,
        game_type,
        player_count,
        finished_at,
        game_players (
          player_position,
          final_score,
          users (
            username
          )
        )
      `)
      .order('finished_at', { ascending: false })
      .limit(limit)

    if (error) {
      console.error('Error getting recent matches:', error)
      return []
    }

    return data.map(game => ({
      ...game,
      winner: game.game_players.find(gp => gp.player_position === 1),
      players: game.game_players.sort((a, b) => a.player_position - b.player_position)
    }))
  } catch (err) {
    console.error('Error in getRecentMatches:', err)
    return []
  }
}

// Get match statistics for a specific game
async function getMatchStatistics(gameId) {
  try {
    const { data, error } = await supabase
      .from('game_players')
      .select(`
        final_score,
        player_position,
        pucks_thrown,
        accuracy_percentage,
        users (
          username
        )
      `)
      .eq('game_id', gameId)
      .order('player_position', { ascending: true })

    if (error) {
      console.error('Error getting match statistics:', error)
      return []
    }

    // Calculate additional statistics
    const totalScore = data.reduce((sum, player) => sum + player.final_score, 0)
    const averageScore = totalScore / data.length
    const highestScore = Math.max(...data.map(p => p.final_score))
    const totalPucksThrown = data.reduce((sum, player) => sum + player.pucks_thrown, 0)
    const averageAccuracy = data.reduce((sum, player) => sum + player.accuracy_percentage, 0) / data.length

    return {
      players: data,
      statistics: {
        totalScore,
        averageScore: Math.round(averageScore * 100) / 100,
        highestScore,
        totalPucksThrown,
        averageAccuracy: Math.round(averageAccuracy * 100) / 100
      }
    }
  } catch (err) {
    console.error('Error in getMatchStatistics:', err)
    return { players: [], statistics: {} }
  }
}

// Update user profile
async function updateUserProfile(clerkUserId, updates) {
  try {
    const { data, error } = await supabase
      .from('users')
      .update(updates)
      .eq('clerk_user_id', clerkUserId)
      .select()
      .single()

    if (error) {
      console.error('Error updating user profile:', error)
      return null
    }

    return data
  } catch (err) {
    console.error('Error in updateUserProfile:', err)
    return null
  }
}

// ===== CONNECTION TEST =====

async function testSupabaseConnection() {
  try {
    const { data, error } = await supabase
      .from('users')
      .select('count', { count: 'exact' })

    if (error) {
      console.error('Supabase connection test failed:', error)
      return false
    }

    console.log('Supabase connected successfully! User count:', data)
    return true
  } catch (err) {
    console.error('Supabase connection error:', err)
    return false
  }
}

// ===== SINGLE EXPORT BLOCK - NO DUPLICATES =====
export {
  createUserProfile,
  getUserProfile,
  saveGameResult,
  saveSinglePlayerGameResult,
  getLeaderboard,
  getUserGameHistory,
  getDetailedMatchInfo,
  createTournament,
  joinTournament,
  getTournaments,
  getUserStatistics,
  getRecentMatches,
  getMatchStatistics,
  updateUserProfile,
  testSupabaseConnection
}
